<template>
    <div>
        <container size="lg">
            <div class="insights-page">
                <section class="header-insights p-x-small-28">
                    <Hero
                        heading-text="Actionable Consumer Insights, Faster Than Ever!"
                        btn-text="Talk to an expert"
                        image="insights/peekage-consumer-insights-company.png"
                        image-alt="Peekage: consumer insights company"
                        hero-content-class="p-t-small-64 p-b-small-48"
                        image-width="w-100"
                    >
                        <template #header="{ headingText }">
                            <div>
                                <Heading
                                    class="f-w-700 m-b-24"
                                    tag="h2"
                                    size="web-x-sm"
                                    style="letter-spacing: -0.5px;"
                                >
                                    Consumer Insight Platform
                                </Heading>

                                <Heading
                                    color="primary"
                                    class="f-w-700 m-b-24"
                                    tag="h2"
                                    size="web-xxx-lg"
                                    style="letter-spacing: -1px;"
                                >
                                    {{ headingText }}
                                </Heading>
                            </div>
                        </template>
                    </Hero>
                </section>

                <section class="p-y-96 p-x-56 p-y-small-48 p-x-small-16">
                    <p class="font f-s-18 f-w-700 m-0 l-h-28 text-center header-p">
                        Peekage is a versatile consumer insight platform that empowers brands to incorporate actionable insights into every aspect of their business, from product development to product launch and growth. With Peekage's fully automated product testing solution, brands of all sizes can efficiently test products and concepts with targeted consumers in natural settings and at scale.
                    </p>
                </section>

                <LogoGarden
                    v-if="$device.mobile"
                    class="p-x-56 p-x-small-16 p-y-small-48 p-y-96"
                />

                <section class="p-y-96 p-x-small-16">
                    <div class="m-b-96">
                        <InsightIn column-class="col-md-3" />
                    </div>
                    <InsightIn header-text="Market Launch & Growth" :card-items="insightInItems" />
                </section>

                <LogoGarden
                    v-if="$device.desktop"
                    class="p-x-56 p-x-small-16 p-y-small-48 p-y-96"
                />

                <section
                    class="m-y-120 m-b-small-80 m-t-small-80"
                    data-aos="fade-in" data-aos-easing="linear"
                >
                    <Feature
                        v-for="item in features"
                        :item="item"
                        :key="item.id"
                    />
                </section>
                <section data-aos="fade-in" data-aos-easing="linear">
                    <HowItWorks :items="howItWorks" />
                </section>
            </div>
        </container>

        <WhyPeekage :items="whyItems" class="m-t-96" />

        <container size="lg" class="p-t-96 p-t-small-60">
            <section data-aos="fade-in" data-aos-easing="linear">
                <CustomerStories :stories="stories" />
            </section>

            <section class="p-l-small-0 p-r-small-0 p-t-96 p-t-small-60" data-aos="fade-in">
                <Banner
                    :btn-text="$device.mobile ? `Let's set a goal` : 'Book a demo'"
                >
                    <template #content>
                        <Heading
                            color="on-color"
                            class="f-w-600 f-s-small-28 l-h-small-36 text-center text-lg-left m-b-0"
                            tag="p"
                            size="x-lg"
                        >
                            Find out how peekage<br>can exceed your business goals
                        </Heading>
                    </template>
                </Banner>
            </section>
        </container>

        <div style="background-color: #F6F6F6;">
            <container size="lg">
                <div class="p-x-small-16">
                    <FaqMain :items="FAQItems" />
                </div>
            </container>
        </div>
    </div>
</template>

<script>
import FaqMain from '~/components/common/faq/FaqMain';
import 'aos/dist/aos.css';
import AOS from 'aos';
import InsightIn from '~/components/common/insights/InsightIn';
import Hero from '~/components/common/hero';
import LogoGarden from '~/components/common/logo-garden/LogoGarden';
import HowItWorks from '~/components/common/how-it-works/HowItWorks';
import Feature from '~/components/common/feature';
import insightsData from '~/content/insights/index';
import WhyPeekage from '~/components/common/why-peekage/why-peekage';
import Banner from '~/components/common/banner';
import screen from '@/plugins/screen';
import CustomerStories from '~/components/common/custormer-stories/CustomerStories';

const _insightInItems = [
    {
        iconPath: 'insights/ROI.svg',
        title: 'Pricing Testing',
        caption: 'Determine the optimal pricing strategy that maximizes revenue while ensuring customer affordability and satisfaction.',
    },
    {
        iconPath: 'insights/Trend.svg',
        title: 'Demand Feasibility Testing',
        caption: 'Demonstrate demand for your products to buyers by validating product demand across various customer segments.',
    },
    {
        iconPath: 'insights/Prioritize.svg',
        title: 'Validating Product Positioning',
        caption: 'Ensure product success by identifying and validating the most effective positioning strategies.',
    },
    {
        iconPath: 'insights/Feedback.svg',
        title: 'Validating Product Messaging',
        caption: 'Optimize marketing efforts and save resources by discovering the most compelling messaging that resonates with your target audience.',
    },
    {
        iconPath: 'insights/User.svg',
        title: 'Validating Buyer Persona',
        caption: 'Identify and validate potential buyer personas to maximize product potential and marketing dollar ROI.',
    },
    {
        iconPath: 'insights/Basket.svg',
        title: 'In-store Experience Optimization',
        caption: 'Gain insights into your in-store experience and improve your distribution, assortment, placement, and overall customer satisfaction.',
    },
];

const _FAQ_ITEMS = [
    {
        title: 'What services does a consumer insights company like Peekage provide?',
        content: 'As a consumer insights company, Peekage provides comprehensive AI-driven product testing services, including in-home usage testing, concept testing, and packaging testing, all in real-time. These solutions help brands make data-driven decisions with confidence. Peekage\'s AI-powered platform enables consumer brand stakeholders to quickly and effectively answer key business questions by testing with targeted consumers at lightning speed, ensuring efficient and impactful decision-making.',
    },
    {
        title: 'What differentiates Peekage as a consumer insights agency?',
        content: 'Peekage stands out as a consumer insights agency by leveraging AI and CPG-trained models to eliminate the need for research knowledge to design consumer studies or rely on data scientists to analyze insights. By integrating AI and automation, Peekage significantly reduces research time, transforming product testing into an accessible and agile tool that can be seamlessly utilized throughout product innovation and renovation cycles.',
    },
    {
        title: 'Can Peekage’s consumer insights be customized to focus on specific demographics or behaviors?',
        content: 'Yes, as a data-driven consumer insights firm, Peekage provides hyper-targeting, allowing brands to recruit desired panels using over 200 consumer attributes ranging from demographic to psychographic data. This tailored approach ensures that brands can conduct targeted consumer studies and gain relevant insights aligned with their unique audiences.',
    },
    {
        title: 'How quickly can our company expect actionable insights from Peekage’s customer insights solution?',
        content: 'Peekage’s customer insights company solution delivers insights in real-time, allowing brands to respond to market shifts and consumer preferences promptly. This agility helps companies stay competitive and make timely decisions based on up-to-date consumer data.',
    },
    {
        title: 'What is the difference between a consumer insights company and traditional market research?',
        content: 'Unlike traditional market research, which may take weeks to recruit panels, gather and analyze data, an AI-driven consumer insights solution like Peekage’s provides rapid, actionable insights through utilizing AI and automation. This means brands can make informed decisions faster, based on the latest consumer data, enhancing their ability to respond dynamically to evolving market needs.',
    },
    {
        title: 'What consumer data services does a company or agency like Peekage offer?',
        content: 'Peekage, as a cutting-edge consumer data services company, provides AI-powered solutions that go beyond traditional market research. Acting as an innovative agency, Peekage delivers real-time insights through in-home product testing, concept validation, and packaging evaluation. By leveraging advanced technology, brands can access precise, actionable consumer data to optimize decision-making, ensuring their products meet market demands and resonate with target audiences.',
    },
];

export default {
    name: 'Insights',
    components: { Hero, InsightIn, LogoGarden, Feature, HowItWorks, Banner, WhyPeekage, CustomerStories, FaqMain },

    mixins: [screen],
    layout: 'xerneas',
    data() {
        return {
            FAQItems: [..._FAQ_ITEMS],
            insightInItems: [],
            features: insightsData.features,
            howItWorks: insightsData.howItWorks,
            whyItems: insightsData.why,
            stories: insightsData.stories,
            structuredDataWebPage: {
                '@context': 'https://schema.org',
                '@type': 'WebPage',
                name: 'Consumer Insights Company - Peekage',
                description: 'Peekage’s Consumer Insights and IHUT suite help brands discover and test new concepts, conduct in-home usage testing, and refine essential elements—such as formula, taste, and packaging—using real consumer feedback in natural settings.',
                url: 'https://peekage.com/consumer-insights-company',
                isPartOf: {
                    '@type': 'WebSite',
                    name: 'Peekage',
                    url: 'https://peekage.com/',
                },
            },
            structuredDataBreadcrumbList: {
                '@context': 'https://schema.org',
                '@type': 'BreadcrumbList',
                itemListElement: [
                    {
                        '@type': 'ListItem',
                        position: 1,
                        name: 'Home',
                        item: 'https://peekage.com/',
                    },
                    {
                        '@type': 'ListItem',
                        position: 2,
                        name: 'Consumer Insights Company',
                        item: 'https://peekage.com/consumer-insights-company',
                    },
                ],
            },
            structuredDataOrganization: {
                '@context': 'https://schema.org',
                '@type': 'Organization',
                name: 'Peekage',
                url: 'https://peekage.com/',
                logo: 'https://peekage.com/logo.png',
                contactPoint: {
                    '@type': 'ContactPoint',
                    telephone: '+1-917-590-4410',
                    contactType: 'Business Inquiries',
                    availableLanguage: ['English'],
                },
                sameAs: [
                    'https://www.linkedin.com/company/peekage/',
                    'https://www.facebook.com/peekage',
                ],
            },
            structuredDataService: {
                '@context': 'https://schema.org',
                '@type': 'Service',
                name: 'Consumer Insights Services',
                provider: {
                    '@type': 'Organization',
                    name: 'Peekage',
                    url: 'https://peekage.com/',
                },
                description: 'Peekage provides data-driven consumer insights solutions to help brands understand customer preferences, behaviors, and market trends.',
                areaServed: 'Global',
                offers: {
                    '@type': 'Offer',
                    price: 'Contact for pricing',
                    priceCurrency: 'USD',
                    url: 'https://peekage.com/consumer-insights-company',
                },
            },
            structuredDataFAQ: {
                '@context': 'https://schema.org',
                '@type': 'FAQPage',
                mainEntity: [
                    {
                        '@type': 'Question',
                        name: 'What services does a consumer insights company like Peekage provide?',
                        acceptedAnswer: {
                            '@type': 'Answer',
                            text: "As a consumer insights company, Peekage provides comprehensive AI-driven product testing services, including in-home usage testing, concept testing, and packaging testing, all in real-time. These solutions help brands make data-driven decisions with confidence. Peekage's AI-powered platform enables consumer brand stakeholders to quickly and effectively answer key business questions by testing with targeted consumers at lightning speed, ensuring efficient and impactful decision-making.",
                        },
                    },
                    {
                        '@type': 'Question',
                        name: 'What differentiates Peekage as a consumer insights agency?',
                        acceptedAnswer: {
                            '@type': 'Answer',
                            text: 'Peekage stands out as a consumer insights agency by leveraging AI and CPG-trained models to eliminate the need for research knowledge to design consumer studies or rely on data scientists to analyze insights. By integrating AI and automation, Peekage significantly reduces research time, transforming product testing into an accessible and agile tool that can be seamlessly utilized throughout product innovation and renovation cycles.',
                        },
                    },
                    {
                        '@type': 'Question',
                        name: 'Can Peekage’s consumer insights be customized to focus on specific demographics or behaviors?',
                        acceptedAnswer: {
                            '@type': 'Answer',
                            text: 'Yes, as a data-driven consumer insights firm, Peekage provides hyper-targeting, allowing brands to recruit desired panels using over 200 consumer attributes ranging from demographic to psychographic data. This tailored approach ensures that brands can conduct targeted consumer studies and gain relevant insights aligned with their unique audiences.',
                        },
                    },
                    {
                        '@type': 'Question',
                        name: 'How quickly can our company expect actionable insights from Peekage’s customer insights solution?',
                        acceptedAnswer: {
                            '@type': 'Answer',
                            text: 'Peekage’s customer insights company solution delivers insights in real-time, allowing brands to respond to market shifts and consumer preferences promptly. This agility helps companies stay competitive and make timely decisions based on up-to-date consumer data.',
                        },
                    },
                    {
                        '@type': 'Question',
                        name: 'What is the difference between a consumer insights company and traditional market research?',
                        acceptedAnswer: {
                            '@type': 'Answer',
                            text: 'Unlike traditional market research, which may take weeks to recruit panels, gather and analyze data, an AI-driven consumer insights solution like Peekage’s provides rapid, actionable insights through utilizing AI and automation. This means brands can make informed decisions faster, based on the latest consumer data, enhancing their ability to respond dynamically to evolving market needs.',
                        },
                    },
                    {
                        '@type': 'Question',
                        name: 'What consumer data services does a company or agency like Peekage offer?',
                        acceptedAnswer: {
                            '@type': 'Answer',
                            text: 'Peekage, as a cutting-edge consumer data services company, provides AI-powered solutions that go beyond traditional market research. Acting as an innovative agency, Peekage delivers real-time insights through in-home product testing, concept validation, and packaging evaluation. By leveraging advanced technology, brands can access precise, actionable consumer data to optimize decision-making, ensuring their products meet market demands and resonate with target audiences.',
                        },
                    },
                ],
            },
        };
    },
    head() {
        return {
            title: 'Real-time Consumer Data - Consumer Insights Company',
            __dangerouslyDisableSanitizers: ['script'],
            script: [
                { innerHTML: JSON.stringify(this.structuredDataWebPage), type: 'application/ld+json' },
                { innerHTML: JSON.stringify(this.structuredDataBreadcrumbList), type: 'application/ld+json' },
                { innerHTML: JSON.stringify(this.structuredDataOrganization), type: 'application/ld+json' },
                { innerHTML: JSON.stringify(this.structuredDataService), type: 'application/ld+json' },
                { innerHTML: JSON.stringify(this.structuredDataFAQ), type: 'application/ld+json' },
            ],
            meta: [
                {
                    hid: 'description',
                    name: 'description',
                    content: "Gain valuable consumer insights & data with Peekage's consumer insights services. Leverage Consumer Data from Development to Launch and Growth.",
                },
                {
                    hid: 'keywords',
                    name: 'keywords',
                    content: 'consumer insights company',
                },
            ],
            link: [
                {
                    rel: 'canonical',
                    href: 'https://peekage.com/consumer-insights-company',
                },
            ],
        };
    },
    beforeMount() {
        this.insightInItems = _insightInItems;
    },
    mounted() {
        AOS.init();
    },
};
</script>
